import { useEffect, useRef, useCallback, useState } from 'react';
import { message } from 'antd';

export interface WebSocketMessage {
  type: string;
  device_id?: string;
  weight?: number;
  unit?: string;
  stable?: boolean;
  timestamp?: number;
  created_at?: string;
  message?: string;
  error?: string;
  source?: string;
  data?: any;
}

export type DeviceType = 'scale' | 'ptl' | 'inkjet-printer' | 'dataman';

interface UseWebSocketOptions {
  deviceType?: DeviceType;
  onMessage?: (msg: WebSocketMessage) => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: Event) => void;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
}

export function useWebSocket(deviceId: string | undefined = undefined, options: UseWebSocketOptions = {}) {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>();
  const reconnectAttemptsRef = useRef(0);
  const isManualCloseRef = useRef(false);
  const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'disconnected' | 'error'>('disconnected');

  const {
    deviceType,
    onMessage,
    onConnect,
    onDisconnect,
    onError,
    reconnectInterval = 3000,
    maxReconnectAttempts = 10,
  } = options;

  // CRITICAL FIX: Use refs for callbacks to prevent dependency loop
  // This prevents the connect function from being recreated on every render
  const onMessageRef = useRef(onMessage);
  const onConnectRef = useRef(onConnect);
  const onDisconnectRef = useRef(onDisconnect);
  const onErrorRef = useRef(onError);

  // Update refs when callbacks change
  useEffect(() => {
    onMessageRef.current = onMessage;
    onConnectRef.current = onConnect;
    onDisconnectRef.current = onDisconnect;
    onErrorRef.current = onError;
  }, [onMessage, onConnect, onDisconnect, onError]);

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const isDev = process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const host = isDev
      ? 'localhost:8051'
      : window.location.host;

    let wsUrl: string;
    if (deviceType && deviceId) {
      wsUrl = `${protocol}//${host}/ws/${deviceType}/${deviceId}`;
    } else if (deviceType) {
      wsUrl = `${protocol}//${host}/ws/${deviceType}`;
    } else {
      wsUrl = `${protocol}//${host}/ws`;
    }

    console.log('[WebSocket] Connecting to:', wsUrl);
    setConnectionStatus('connecting');

    try {
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        console.log('[WebSocket] Connected');
        setConnectionStatus('connected');
        reconnectAttemptsRef.current = 0;
        onConnectRef.current?.();
      };

      wsRef.current.onmessage = (event) => {
        try {
          const data: WebSocketMessage = JSON.parse(event.data);
          onMessageRef.current?.(data);
        } catch (error) {
          console.error('[WebSocket] Failed to parse message:', error);
        }
      };

      wsRef.current.onclose = (event) => {
        console.log('[WebSocket] Disconnected:', event.code, event.reason);
        setConnectionStatus('disconnected');
        onDisconnectRef.current?.();

        if (!isManualCloseRef.current &&
            reconnectAttemptsRef.current < maxReconnectAttempts) {
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttemptsRef.current++;
            console.log(`[WebSocket] Reconnecting... attempt ${reconnectAttemptsRef.current}/${maxReconnectAttempts}`);
            connect();
          }, reconnectInterval);
        }
      };

      wsRef.current.onerror = (error) => {
        console.error('[WebSocket] Error:', error);
        setConnectionStatus('error');
        onErrorRef.current?.(error);
      };

    } catch (error) {
      console.error('[WebSocket] Failed to create connection:', error);
      setConnectionStatus('error');
      message.error('Failed to connect to real-time updates');
    }
  }, [deviceId, deviceType, reconnectInterval, maxReconnectAttempts]);

  const disconnect = useCallback(() => {
    console.log('[WebSocket] Manual disconnect');
    isManualCloseRef.current = true;
    setConnectionStatus('disconnected');
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close(1000, 'Client closing');
      wsRef.current = null;
    }
  }, []);

  const sendMessage = useCallback((msg: any) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(msg));
    } else {
      console.warn('[WebSocket] Cannot send message: not connected');
    }
  }, []);

  const subscribe = useCallback((deviceIds: string | string[]) => {
    const ids = Array.isArray(deviceIds) ? deviceIds : [deviceIds];
    ids.forEach(id => {
      sendMessage({ type: 'subscribe', device_id: id });
    });
  }, [sendMessage]);

  const unsubscribe = useCallback((deviceIds: string | string[]) => {
    const ids = Array.isArray(deviceIds) ? deviceIds : [deviceIds];
    ids.forEach(id => {
      sendMessage({ type: 'unsubscribe', device_id: id });
    });
  }, [sendMessage]);

  useEffect(() => {
    connect();
    return () => disconnect();
  }, [connect, disconnect]);

  return {
    sendMessage,
    subscribe,
    unsubscribe,
    disconnect,
    connected: connectionStatus === 'connected',
    connecting: connectionStatus === 'connecting',
    connectionStatus,
  };
}
